import * as React from "react";
import Helmet from "react-helmet";
import { Link, RouteComponentProps, withRouter } from "react-router-dom";
import styled from "styled-components";

import { Button, Paragraph } from "../../../../components/atoms";
import { Input } from "../../../../components/molecules";
import {
  InputPhoneCodeContainer,
  IPhoneNumberObject,
} from "../../../../containers/common/input-phone-code";
import { SelectorContainer } from "../../../../containers/common/selector";
import {
  getCities,
  IApiSignupOtpType,
  ILoginUser,
  signupCustomer,
} from "../../../../services/api/customer";
import { ApiError } from "../../../../services/error-handler";
import { codesData } from "../../../../static/country-telephone-codes";
import { theme } from "../../../../theme";

const StyledLink = styled(Link)`
  color: #666666;
  text-decoration: underline;
`;

const ErrorParagraph = Paragraph.extend`
  color: ${theme.color.error};

  a {
    color: ${theme.color.error};
  }
`;

const ContinueButton = styled(Button)`
  width: 100%;
  margin-top: 0px;
`;

const FormRow = styled.div`
  margin: 16px 0;
  display: flex;
  flex-direction: row;

  > div {
    flex: 1;
    margin: 0 8px;

    &:first-child {
      margin-left: 0;
    }

    &:last-child {
      margin-right: 0;
    }
  }
`;

const MarketingParagraph = Paragraph.extend`
  padding-left: 38px;
  text-indent: -38px;
`;

const MarketingInput = styled.input`
  width: 24px;
  height: 24px;
  margin-right: 10px;
  vertical-align: middle;
`;

interface IOwnProps {
  onLogIn(user: ILoginUser): void;
}

interface IState {
  email: string;
  errorMessage?: string;
  firstName: string;
  fullPhoneNumber: string;
  hasVerifiedEmail: boolean;
  hasVerifiedPhone: boolean;
  lastName: string;
  otpCode: string;
  otpRequestId?: string;
  otpType: IApiSignupOtpType;
  phoneCodeIndex?: number;
  phoneNumber: string;
  shouldShowDuplicateEmailError: boolean;
  shouldShowDuplicatePhoneError: boolean;
  agreeMailListSignup: boolean;
  agreeTermsAndConditions: boolean;
  city: string;
  cities: string[];
}

interface ILocationState {
  email: string;
  otpCode: string;
  otpRequestId: string;
  phoneCodeIndex: number;
  phoneNumber: string;
}

type IProps = IOwnProps & RouteComponentProps<{}, {}, ILocationState>;

class RegisterComponent extends React.Component<IProps, IState> {
  private readonly minPhoneLength = 5;

  public state: IState = {
    email: "",
    errorMessage: undefined,
    firstName: "",
    fullPhoneNumber: "",
    hasVerifiedEmail: false,
    hasVerifiedPhone: false,
    lastName: "",
    otpCode: "",
    otpRequestId: undefined,
    otpType: "email",
    phoneCodeIndex: undefined,
    phoneNumber: "",
    shouldShowDuplicateEmailError: false,
    shouldShowDuplicatePhoneError: false,
    agreeMailListSignup: false,
    agreeTermsAndConditions: false,
    city: "",
    cities: [],
  };

  public async componentDidMount(): Promise<void> {
    if (typeof this.props.location.state !== "undefined") {
      const { email, phoneNumber, phoneCodeIndex, otpRequestId, otpCode } =
        this.props.location.state;

      if (
        typeof otpRequestId !== "undefined" &&
        typeof otpCode !== "undefined"
      ) {
        this.setState({ otpRequestId, otpCode });
      }

      if (
        typeof phoneNumber !== "undefined" &&
        typeof phoneCodeIndex !== "undefined"
      ) {
        const fullPhoneNumber = `${codesData[phoneCodeIndex].dial_code}${phoneNumber}`;
        this.setState({
          phoneCodeIndex,
          phoneNumber,
          fullPhoneNumber,
          hasVerifiedPhone: true,
          otpType: "phone",
        });
      }

      if (typeof email !== "undefined") {
        this.setState({ email, hasVerifiedEmail: true });
      }
    }

    this.setState({ cities: await getCities() });
  }

  /**
   * Handle submission of the registration form
   */
  public handleSubmit = async (): Promise<void> => {
    const {
      email,
      firstName,
      lastName,
      otpCode,
      otpType,
      fullPhoneNumber,
      agreeMailListSignup,
      city,
    } = this.state;

    try {
      if (this.state.otpRequestId) {
        this.setState({
          errorMessage: undefined,
          shouldShowDuplicateEmailError: false,
          shouldShowDuplicatePhoneError: false,
        });

        const user = await signupCustomer({
          email,
          firstName,
          lastName,
          otpCode,
          otpRequestId: this.state.otpRequestId,
          otpType,
          phone: fullPhoneNumber,
          agreeMailListSignup: !agreeMailListSignup,
          city,
        });

        this.props.onLogIn(user);
      }
    } catch (err) {
      if (err instanceof ApiError) {
        if (
          err instanceof ApiError &&
          err.code === "SIGNUP_EMAIL_ALREADY_IN_USE"
        ) {
          this.setState({ shouldShowDuplicateEmailError: true });
        } else if (
          err instanceof ApiError &&
          err.code === "SIGNUP_PHONE_ALREADY_IN_USE"
        ) {
          this.setState({ shouldShowDuplicatePhoneError: true });
        } else {
          this.setState({ errorMessage: err.message });
        }
      }
    }
  };

  public handleChangePhoneNumber = (phone: IPhoneNumberObject): void => {
    this.setState({
      fullPhoneNumber: phone.fullPhoneNumber,
    });
  };

  // tslint:disable-next-line: completed-docs
  public render(): JSX.Element {
    const {
      email,
      firstName,
      lastName,
      fullPhoneNumber,
      agreeMailListSignup,
      agreeTermsAndConditions,
      city,
    } = this.state;
    const isContinueDisabled = !Boolean(
      email &&
        firstName &&
        lastName &&
        fullPhoneNumber.length > this.minPhoneLength &&
        agreeTermsAndConditions &&
        city,
    );
    let error: JSX.Element | undefined;

    if (typeof this.state.errorMessage !== "undefined") {
      error = <ErrorParagraph>{this.state.errorMessage}</ErrorParagraph>;
    } else if (this.state.shouldShowDuplicateEmailError) {
      error = (
        <ErrorParagraph>
          That email is already registered with us.{" "}
          <Link to="/booking/login/email">Click here to log in</Link>.
        </ErrorParagraph>
      );
    } else if (this.state.shouldShowDuplicatePhoneError) {
      error = (
        <ErrorParagraph>
          That phone number is already registered with us.{" "}
          <Link to="/booking/login/phone">Click here to log in</Link>.
        </ErrorParagraph>
      );
    }

    return (
      <React.Fragment>
        <Helmet>
          <title>Ruuby - Checkout - Register</title>
        </Helmet>

        <Paragraph>You need to create an account with us to continue</Paragraph>

        <FormRow>
          <Input
            label="First Name"
            name="firstName"
            onChange={(e) =>
              this.setState({ firstName: e.currentTarget.value })
            }
            value={firstName}
            required
          />

          <Input
            label="Last Name"
            name="lasttName"
            onChange={(e) => this.setState({ lastName: e.currentTarget.value })}
            value={lastName}
            required
          />
        </FormRow>

        <FormRow>
          <SelectorContainer
            placeholder="City"
            items={this.state.cities}
            selectedItem={this.state.city}
            onSelect={(item: string) => this.setState({ city: item })}
          />
        </FormRow>

        <FormRow>
          <Input
            label="Email"
            name="email"
            onChange={(e) => this.setState({ email: e.currentTarget.value })}
            value={email}
            required
            disabled={this.state.hasVerifiedEmail}
          />
        </FormRow>

        <FormRow>
          <InputPhoneCodeContainer
            onNumberChange={this.handleChangePhoneNumber}
            preselectedCodeIndex={this.state.phoneCodeIndex}
            preselectedPhoneNumber={this.state.phoneNumber}
            disabled={this.state.hasVerifiedPhone}
          />
        </FormRow>

        <FormRow style={{ marginBottom: "0px", paddingBottom: "0px" }}>
          <MarketingParagraph style={{ marginBottom: "0px" }}>
            <MarketingInput
              id="signup-marketing-input"
              type="checkbox"
              checked={agreeMailListSignup}
              onChange={(event) =>
                this.setState({ agreeMailListSignup: event.target.checked })
              }
            />
            <label htmlFor="signup-marketing-input">
              If you <strong>do not</strong> wish to receive marketing emails
              with the latest Ruuby news, offers and discounts, please check
              this box. For further information on how we treat your personal
              information, please see our 
              <StyledLink to="/privacy-policy" target="_blank">
                Privacy Policy
              </StyledLink>
              . We won't share your data and you can unsubscribe at any time.
            </label>
          </MarketingParagraph>
        </FormRow>

        <FormRow style={{ marginTop: "0px" }}>
          <MarketingParagraph>
            <MarketingInput
              id="terms-and-conditions-input"
              type="checkbox"
              checked={agreeTermsAndConditions}
              onChange={(event) =>
                this.setState({ agreeTermsAndConditions: event.target.checked })
              }
            />
            <label htmlFor="terms-and-conditions-input">
              By ticking this box I agree to{" "}
              <StyledLink to="/terms_and_conditions" target="_blank">
                Ruuby's Terms and Conditions
              </StyledLink>
            </label>
          </MarketingParagraph>
        </FormRow>
        <ContinueButton size="small" onClick={this.handleSubmit} disabled={isContinueDisabled}>Continue</ContinueButton>
        {error}
      </React.Fragment>
    );
  }
}
export const Register = withRouter(RegisterComponent);
